import { getApiBaseUrl } from '../../utils/environment.mjs';
import { useState, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import InvoiceService from "../service/invoiceUploadService.mjs";
import { AuthContext } from "../../authProvider.mjs";
import { secureLogger } from "../../utils/secureLogger.mjs";



const _API_URL = getApiBaseUrl();

// The server's per-file limit (multer, invoiceController).
const MAX_FILE_BYTES = 10 * 1024 * 1024;

// Cloud Run refuses any HTTP/1 request over 32 MiB before it reaches the
// server, and that refusal has no CORS headers, so the browser saw only
// "Network Error" and the whole batch was lost. Ten files may each be 10 MB,
// so a selection goes up in requests of at most this much, one after another,
// all into the same claim. The margin covers the multipart overhead.
const MAX_REQUEST_BYTES = 28 * 1024 * 1024;

/** Split files, in order, into groups whose sizes add up to MAX_REQUEST_BYTES at most. */
const splitIntoRequests = (files) => {
  const batches = [];
  let current = [];
  let currentBytes = 0;
  for (const file of files) {
    const size = file.size || 0;
    if (current.length > 0 && currentBytes + size > MAX_REQUEST_BYTES) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(file);
    currentBytes += size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
};

const sumBytes = (files) => files.reduce((sum, f) => sum + (f.size || 0), 0);

const useInvoiceUpload = (onExtracted = null, existingClaimId = null) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionComplete, setExtractionComplete] = useState(false);
  const [error, setError] = useState(null);
  const [extractedData, setExtractedData] = useState([]);
  const { user } = useContext(AuthContext);
  const [processedItems, setProcessedItems] = useState([]);
  const [claimId, setClaimId] = useState(null);

  // Handle file selection
  const handleFileSelect = useCallback((selectedFiles) => {
    const incoming = Array.from(selectedFiles || []);
    if (incoming.length === 0) return;

    // PDF, JPEG or PNG. Accept on either the MIME type or the extension, since
    // some browsers report an empty (or generic) type for files dragged in from
    // certain sources. The server checks the file's actual bytes, so this is
    // only here to tell the user early. HEIC, WebP and TIFF are not supported.
    const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const isAllowed = (file) =>
      ALLOWED_TYPES.includes(String(file.type || '').toLowerCase()) ||
      /\.(pdf|jpe?g|png)$/i.test(String(file.name || ''));

    const allowedFiles = incoming.filter(isAllowed);
    const rejected = incoming.filter(f => !isAllowed(f));

    // The server refuses any file over 10 MB, and one such file used to fail
    // the whole batch with an error that did not name it. Phone photos can be
    // that big, so they are held back here, by name.
    const tooBig = allowedFiles.filter(f => f.size > MAX_FILE_BYTES);
    const accepted = allowedFiles.filter(f => f.size <= MAX_FILE_BYTES);

    // Tell the user exactly what was blocked instead of silently dropping it.
    const problems = [];
    if (rejected.length > 0) {
      const names = rejected.map(f => f.name).join(', ');
      problems.push(
        rejected.length === incoming.length
          ? `Only PDF, JPG or PNG files can be uploaded. Rejected: ${names}`
          : `Only PDF, JPG or PNG files can be uploaded - these were skipped: ${names}`
      );
    }
    if (tooBig.length > 0) {
      problems.push(`Each file must be 10 MB or smaller - these were skipped: ${tooBig.map(f => f.name).join(', ')}`);
    }
    if (problems.length > 0) {
      setError(problems.join(' '));
      if (accepted.length === 0) return;
    }

    if (files.length + accepted.length > 10) {
      setError('You can only upload up to 10 files.');
      return;
    }

    setFiles(prevFiles => [...prevFiles, ...accepted]);
    if (problems.length === 0) setError(null);
  }, [files]);

  // Remove a file from the list
  const removeFile = useCallback((index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  }, []);

  // Upload files to server
  const uploadFiles = useCallback(async () => {
    if (files.length === 0) {
      setError('Please select at least one file to upload.');
      return;
    }

    if (!user) {
      setError('User authentication required. Please log in again.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setIsExtracting(false);
    setExtractionComplete(false);
    setError(null);

    const batches = splitIntoRequests(files);
    const totalBytes = sumBytes(files) || 1;
    let sentBytes = 0;
    let sentCount = 0;
    // Local, not the state: it does not update inside this call, and every
    // request after the first must append to the claim the first one created.
    let targetClaimId = existingClaimId || claimId;
    const allItems = [];
    // Files the server could not store, by name (it returns only names).
    const failedNames = [];

    // The failed files to leave in the list for a retry. Only a name that is
    // unique in this selection says which file it was (iOS calls every photo
    // image.jpg); the others are named in the message for the user to pick again.
    const retryableOf = (sent) => sent.filter(f =>
      failedNames.includes(f.name) && files.filter(g => g.name === f.name).length === 1
    );
    const failedMessage = () =>
      `These files could not be saved and were not added - please upload them again: ${failedNames.join(', ')}`;

    try {
      const service = new InvoiceService();
      // These requests only store the files and create one line per file;
      // reading the invoices happens afterwards, row by row, from the form.
      // The bar follows the real bytes sent across all requests, and once the
      // last one is sent the indicator switches to "Creating line items…"
      // while the server finishes. Both used to be faked with timers that knew
      // nothing about the upload.
      for (let b = 0; b < batches.length; b += 1) {
        const batch = batches[b];
        const isLast = b === batches.length - 1;
        const batchStart = sentBytes;
        const batchBytes = sumBytes(batch);
        const response = await service.uploadInvoices(batch, targetClaimId, {
          onUploadProgress: (event) => {
            if (!event || !event.total) return;
            const done = batchStart + batchBytes * Math.min(1, event.loaded / event.total);
            setUploadProgress(Math.min(100, (done / totalBytes) * 100));
            if (isLast && event.loaded >= event.total) {
              setIsUploading(false);
              setIsExtracting(true);
            }
          }
        });

        if (!response || !response.claimId) {
          throw new Error('Invalid response from server');
        }

        sentBytes += batchBytes;
        sentCount += batch.length;
        targetClaimId = response.claimId;
        setClaimId(response.claimId);
        if (Array.isArray(response.failed)) failedNames.push(...response.failed);

        const items = response.items || [];
        allItems.push(...items);
        // Hand each stored request's rows over straight away, so if a later
        // request fails the lines already created on the server are still on
        // the form. Third argument: whether the form should read each
        // invoice - the server decides this from the claim owner's region.
        if (typeof onExtracted === 'function' && items.length > 0) {
          onExtracted(items, response.claimId, {
            extractionEnabled: response.extractionEnabled === true
          });
        }
      }

      setUploadProgress(100);

      // A batch should always come back as one line per file. Nothing means the
      // upload itself failed - say so rather than reporting success with an
      // empty table.
      if (allItems.length === 0) {
        setIsUploading(false);
        setIsExtracting(false);
        setExtractionComplete(false);
        setUploadProgress(0);
        setError(
          failedNames.length > 0
            ? failedMessage()
            : 'The upload completed but no line items were created. ' +
              'Please try again, or add the lines manually.'
        );
        return;
      }

      // Store processed claim data
      setExtractedData(allItems);

      // The rows were handed over as each request finished. There used to be
      // 2.3s of fixed delays here purely so a "done" state stayed on screen;
      // the rows appearing in the table is the signal now, and each one
      // reports its own reading progress.
      // Either an embedding form already has the items, or (standalone)
      // redirect to the expense form with the data in navigation state.
      if (typeof onExtracted === 'function') {
        // Reusable uploader: clear this batch (but keep the claim linkage)
        // so the user can immediately add another set of up to 10 invoices.
        // Files the server could not store stay listed, and are named, so a
        // partly failed batch is not mistaken for a complete one.
        setFiles(retryableOf(files));
        setUploadProgress(0);
        setIsUploading(false);
        setIsExtracting(false);
        setExtractionComplete(false);
        setError(failedNames.length > 0 ? failedMessage() : null);
      } else {
        setIsUploading(false);
        setIsExtracting(false);
        navigate(`/expense/claims/${targetClaimId}`, {
          state: {
            extractedData: allItems,
            claimId: targetClaimId
          }
        });
      }

    } catch (err) {
      secureLogger.error('Upload error', { status: err.response?.status });

      let errorMessage = 'Failed to upload files. Please try again.';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.isAxiosError && !err.response) {
        // No response at all: offline, or refused before reaching the server.
        // axios only says "Network Error".
        errorMessage = 'The upload could not reach the server. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Earlier requests may already have created their lines. Keep only the
      // files that were not stored, so a retry does not add those lines twice.
      if (sentCount > 0) {
        const retry = retryableOf(files.slice(0, sentCount));
        setFiles(prev => [...retry, ...prev.slice(sentCount)]);
        if (allItems.length > 0) {
          errorMessage = `Some of the files were added to the claim and are no longer listed. ${errorMessage}`;
        }
        if (failedNames.length > 0) errorMessage += ` ${failedMessage()}`;
      }

      setError(errorMessage);
      setIsUploading(false);
      setIsExtracting(false);
      setExtractionComplete(false);
      setUploadProgress(0);
    }
  }, [files, navigate, user, onExtracted, existingClaimId, claimId]);

  // Reset the form
  const resetForm = useCallback(() => {
    setFiles([]);
    setIsUploading(false);
    setUploadProgress(0);
    setIsExtracting(false);
    setExtractionComplete(false);
    setError(null);
    setExtractedData([]);
    setClaimId(null);
    setProcessedItems([]);
  }, []);

  return {
    files,
    isUploading,
    uploadProgress,
    isExtracting,
    extractionComplete,
    error,
    extractedData,
    claimId,
    processedItems,
    handleFileSelect,
    removeFile,
    uploadFiles,
    resetForm
  };
};

export default useInvoiceUpload;


